// What worked a plan's issues (Poulpe-761, today's Poulpe-128): the fresh
// tokens their sessions reported, and the agents and models that ran them.
// The snapshot and the feed carry every session, ended ones included, with
// its last usage report, so this reads the store alone. Each session names one
// issue, so it counts once in any total.
import type { Runner, Session } from '../api/types'
import { concrete } from '../code/choices'
import { catalogueName, catalogueOf } from '../code/SubAgentSettings'
import { useBoardState } from '../store/context'

/** A model as a row says it: its display name, its vendor's name left out, and its full id. */
export interface ModelUsed {
  id: string
  label: string
}

/** One engine (Claude, Codex, a local model) and the models its sessions ran, oldest first. */
export interface EngineWork {
  engine: string
  models: ModelUsed[]
}

export interface IssueWork {
  /** The sum of its sessions' fresh tokens; null when none reported any. */
  tokens: number | null
  /** By engine, in name order. */
  engines: EngineWork[]
}

const ENGINES: Readonly<Record<string, string>> = { anthropic: 'claude', openai: 'codex', local: 'local' }

/** The engine a session's vendor runs: `claude`, `codex`, `local`, or the vendor as it is named. */
export function engineOfVendor(vendor: string): string {
  return Object.hasOwn(ENGINES, vendor) ? ENGINES[vendor]! : vendor
}

/** A vendor's or an agent's name at the start of a display name: the engine's dot says it already. */
const VENDOR_PREFIX = /^(claude|codex|openai|anthropic)\s+/i
/** A model id or name with no version: an alias (`default`, `claude`, `opus`) that says no model. */
const hasVersion = (text: string) => /\d/.test(text)

/**
 * The model a session ran, as its agent's model choices name it (the
 * session's own settings, then its runner's catalogue, as the Code tab reads
 * them), else its id. The settings' current model goes before the pinned one.
 * Its vendor's name is dropped (`Claude Opus 5.5` reads `Opus 5.5`). Null for
 * a bare alias that names no known model.
 */
export function modelUsed(session: Session, runners: readonly Runner[]): ModelUsed | null {
  const current = session.settings?.current_model
  const id = concrete(current) ? current : session.agent.model
  if (!concrete(id)) return null
  const named = catalogueName(catalogueOf(session, runners), id)
  if (named && concrete(named) && hasVersion(named)) return { id, label: named.replace(VENDOR_PREFIX, '') }
  return hasVersion(id) ? { id, label: id } : null
}

const oldestFirst = (a: Session, b: Session) => a.created_at.localeCompare(b.created_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/** Every issue's work, from the sessions that name it, ended ones included. */
export function issueWork(sessions: Iterable<Session>, runners: readonly Runner[]): Map<string, IssueWork> {
  const work = new Map<string, { tokens: number | null; engines: Map<string, Map<string, ModelUsed>> }>()
  for (const session of [...sessions].sort(oldestFirst)) {
    if (!session.issue_id) continue
    let row = work.get(session.issue_id)
    if (!row) work.set(session.issue_id, (row = { tokens: null, engines: new Map() }))
    const tokens = session.usage?.new_tokens
    if (tokens !== undefined && Number.isFinite(tokens) && tokens >= 0) row.tokens = (row.tokens ?? 0) + tokens
    const engine = engineOfVendor(session.agent.vendor)
    let models = row.engines.get(engine)
    if (!models) row.engines.set(engine, (models = new Map()))
    const model = modelUsed(session, runners)
    if (model && !models.has(model.label)) models.set(model.label, model)
  }
  const out = new Map<string, IssueWork>()
  for (const [issue, row] of work) {
    const engines = [...row.engines.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([engine, models]) => ({ engine, models: [...models.values()] }))
    out.set(issue, { tokens: row.tokens, engines })
  }
  return out
}

/** Several issues' tokens: their sum, how many reported any, and how many were worked at all. Each issue counts once. */
export function sumTokens(work: ReadonlyMap<string, IssueWork>, issueIds: Iterable<string>): { tokens: number | null; measured: number; worked: number } {
  let tokens: number | null = null
  let measured = 0
  let worked = 0
  for (const id of new Set(issueIds)) {
    const row = work.get(id)
    if (!row) continue
    worked++
    if (row.tokens === null) continue
    measured++
    tokens = (tokens ?? 0) + row.tokens
  }
  return { tokens, measured, worked }
}

// Every phase card and the plan's numbers read the same work: one reading per
// change of the store's sessions or runners, shared.
const read = new WeakMap<ReadonlyMap<string, Session>, { runners: ReadonlyMap<string, Runner>; work: Map<string, IssueWork> }>()

/** The store's sessions' work by issue, once per change of the sessions or the runners. */
export function useIssueWork(): Map<string, IssueWork> {
  const { sessions, runners } = useBoardState()
  const held = read.get(sessions)
  if (held && held.runners === runners) return held.work
  const work = issueWork(sessions.values(), [...runners.values()])
  read.set(sessions, { runners, work })
  return work
}
